import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useIsRTL } from "@/hooks";
import { getDeepSelectedCategory } from "@/utils";
import { selectedFiltersContext as SelectedFiltersMobContext } from "@/routes/productListing/productListingFiltersMob";
import {
  buildFacetMapByIdentity,
  getFacetIdentity,
  normalizeQueryParamsForCompare,
  normalizeSelectedOptionsByQueryParam
} from "@/routes/productListing/utils";
import FiltersTypeSection from "@/routes/productListing/productListingFiltersMob/filtersTypeSection";
import { ActionButtons } from "@/routes/productListing/productListingFiltersMob/actionButtons";
import FilterWrapperElement from "./filterWrapperElement";
import FilterContainer from "./filtersContainer";
import { Facet, FilterType } from "../types";
import { SalePageDataContext } from "../../SaleWrapper";
import { FILTER_TYPES } from "../constants";

const TABLET_WIDTH = 812;

/** Match PLP: category facets may omit root queryParam (only tree leaves carry it). */
const isRenderableFacet = (value: unknown): value is Facet => {
  if (!value || typeof value !== "object") return false;
  const f = value as any;
  if (!f.type) return false;
  if (f.type === FILTER_TYPES.MORE_FILTERS) return true;
  if (f.type === FILTER_TYPES.CATEGORY) {
    const data = (f as { data?: unknown }).data;
    return Array.isArray(data) && data.length > 0;
  }
  return typeof f.queryParam === "string";
};

const FilterSidebar: FC<{
  filters: Facet[];
  isFiltersOpen: boolean;
  onFiltersClose: (applied?: boolean) => void;
  itemsTotal?: number;
}> = ({ filters, isFiltersOpen, onFiltersClose }) => {
  const [toggledFilter, setToggledFilter] = useState<
    FilterType | Facet | EmptyObject | null
  >(null);
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const isRTL = useIsRTL();
  const { t } = useTranslation("productListing");
  const { selectedFiltersQuery } = useContext(SalePageDataContext);
  const { onSelection, setSelectedFilters, selectedFilters } = useContext(
    SelectedFiltersMobContext
  );

  const activeFilter = isRenderableFacet(toggledFilter) ? toggledFilter : null;

  const applyStartFiltersRef = useRef<Facet[] | null>(null);
  const applyStartQuerySnapshotRef = useRef<Record<string, string[]> | null>(
    null
  );

  const filterById = useMemo(() => {
    const map = buildFacetMapByIdentity<Facet>(filters);
    const moreFiltersContainer = (filters as any[]).find(
      (f: any) => f.type === FILTER_TYPES.MORE_FILTERS
    );
    (moreFiltersContainer?.data || []).forEach((item: any) => {
      map.set(getFacetIdentity(item), item as Facet);
    });
    return map;
  }, [filters]);

  const categoryFilter = useMemo(
    () => filters.find(f => f.type === FILTER_TYPES.CATEGORY) ?? null,
    [filters]
  );

  const selectedCategoryCount = useMemo(
    () =>
      categoryFilter
        ? getDeepSelectedCategory(categoryFilter as any).length
        : 0,
    [categoryFilter]
  );

  const selectedFiltersNorm = useMemo(
    () => normalizeSelectedOptionsByQueryParam(selectedFilters, ["q"]),
    [selectedFilters]
  );
  const selectedQueryNorm = useMemo(
    () => normalizeQueryParamsForCompare(selectedFiltersQuery),
    [selectedFiltersQuery]
  );

  // After apply: wait for data update and if target filter still exists,
  // switch to it.
  useEffect(() => {
    if (!isApplying || !pendingTargetId) return;
    const queryChangedSinceApplyStart =
      !!applyStartQuerySnapshotRef.current &&
      !isEqual(selectedQueryNorm, applyStartQuerySnapshotRef.current);
    const filtersPropChangedSinceApplyStart =
      !!applyStartFiltersRef.current &&
      applyStartFiltersRef.current !== filters;

    if (!queryChangedSinceApplyStart || !filtersPropChangedSinceApplyStart) {
      return;
    }

    const next = filterById.get(pendingTargetId);
    if (next) {
      setToggledFilter(next);
    }
    setPendingTargetId(null);
    setIsApplying(false);
    applyStartFiltersRef.current = null;
    applyStartQuerySnapshotRef.current = null;
  }, [isApplying, pendingTargetId, selectedQueryNorm, filters, filterById]);

  useEffect(() => {
    !isFiltersOpen && setToggledFilter(filters[0]);
  }, [isFiltersOpen, filters]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > TABLET_WIDTH) {
        onFiltersClose();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const closeFilter = () => {
    onFiltersClose();
  };

  const clearAllSelectedFilters = () => {
    if (!isEmpty(selectedFilters) || !isEmpty(selectedFiltersQuery)) {
      const clearedFilters = Object.keys(selectedFilters).map(item => {
        return { queryParam: item, selectedOptions: [] };
      });
      onSelection(clearedFilters);
      setSelectedFilters({});
    }
  };

  const onSelectCategory = () => {
    if (categoryFilter) onFilterTitleClick(categoryFilter);
  };

  const onFilterTitleClick = (nextFilter: Facet) => {
    const hasPendingChanges = !isEqual(selectedFiltersNorm, selectedQueryNorm);

    if (!hasPendingChanges) {
      setPendingTargetId(null);
      setToggledFilter(nextFilter);
      return;
    }

    setPendingTargetId(getFacetIdentity(nextFilter));
    setIsApplying(true);
    applyStartFiltersRef.current = filters;
    applyStartQuerySnapshotRef.current = selectedQueryNorm;
    onSelection();
  };

  const onApplyAndClose = () => {
    const hasPendingChanges = !isEqual(selectedFiltersNorm, selectedQueryNorm);

    setPendingTargetId(null);
    setIsApplying(false);
    applyStartFiltersRef.current = null;
    applyStartQuerySnapshotRef.current = null;

    if (hasPendingChanges) {
      onSelection();
    }
    onFiltersClose(true);
  };

  return (
    <div className="sidebar_container">
      <div
        className={cn("sidebar", {
          open: isFiltersOpen
        })}
        style={{ width: "100%" }}
      >
        <div className="sidebar_content">
          <FilterWrapperElement
            iconClassName="close"
            onIconClick={closeFilter}
            filterTitle={t("filterTitle")}
            onClearBtnClick={clearAllSelectedFilters}
            isHeaderVisible={true}
          >
            <div className="mobile_filter_sheet">
              <div className={cn("filter_wrapper", { arabic: isRTL })}>
                <FiltersTypeSection
                  filters={filters}
                  selectedFilters={selectedFilters}
                  toggledFilter={toggledFilter}
                  setToggledFilter={setToggledFilter}
                  onFilterClick={onFilterTitleClick}
                />
                <div className="filter_content_section">
                  {activeFilter && (
                    <ErrorBoundary fallback={null}>
                      <FilterContainer
                        filter={activeFilter}
                        setToggledFilter={setToggledFilter}
                        toggledFilter={activeFilter as any}
                        onApplyButtonClick={onSelection}
                        onSelectCategory={onSelectCategory}
                        selectedCategoryCount={selectedCategoryCount}
                      />
                    </ErrorBoundary>
                  )}
                </div>
              </div>
              <ActionButtons
                onCloseButtonClick={closeFilter}
                onApplyButtonClick={onApplyAndClose}
              />
            </div>
          </FilterWrapperElement>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
